import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTaskCompletion } from '../../hooks/useTaskCompletion';
import { toast } from 'sonner';
import { useCanAccessFeature } from '../../hooks/usePermissions';
import { nudgeTask } from '../../services/tasks/taskCollaborationService';
import { CheckCircle2, Plus, RotateCcw, Tag, Trash2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/textarea';
import {
  type Task,
  type TaskSection,
  useUpdateTask,
} from '../../hooks/queries/useTasks';
import { useUndoableTaskDelete } from '../../hooks/useUndoableTaskDelete';
import { useTaskAssignees } from '../../hooks/queries/useTaskCollaboration';
import { getPriorityMeta } from './taskHelpers';
import { extractDueDay } from '../../lib/tasks/taskReschedule';
import { applyFicheDueDay, ficheDueTime } from '../../lib/tasks/taskDueTime';
import { DatePicker } from '../ui/DatePicker';
import { readableTextColor } from '../../lib/colorContrast';
import { TaskAssigneeDropdown } from './TaskAssigneeDropdown';
import { AssigneeAvatars } from './AssigneeAvatars';
import { TaskSecondaryAccordion } from './TaskSecondaryAccordion';
import { useEffectiveTaskFields } from '../../hooks/useEffectiveTaskFields';
import { useTaskTags, useTaskTagsFor, useSetTaskTags, useCreateTaskTag } from '../../hooks/queries/useTaskTags';

import { formatAppDate } from '../../lib/dateFormat';
// Palette partagée avec l'onglet Paramètres > Tags.
export const TAG_PALETTE = [
  '#64748b', '#0891b2', '#0d9488', '#16a34a',
  '#ca8a04', '#dc2626', '#9333ea', '#db2777',
];

interface TaskEditModalProps {
  task: Task | null;
  sections: TaskSection[];
  onClose: () => void;
}

const PRIORITIES = [1, 2, 3, 4] as const;

export function TaskEditModal({ task, sections, onClose }: TaskEditModalProps) {
  const updateTask = useUpdateTask();
  const requestDelete = useUndoableTaskDelete();
  const completion = useTaskCompletion();
  const { data: assignees = [] } = useTaskAssignees(task?.id ?? null);

  const [content, setContent] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState(3);
  const [sectionId, setSectionId] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [deadline, setDeadline] = useState('');

  const { data: allTags = [] } = useTaskTags();
  const { data: taskTagMap = {} } = useTaskTagsFor(task ? [task.id] : []);
  const setTaskTags = useSetTaskTags();
  const createTag = useCreateTaskTag();
  const fields = useEffectiveTaskFields();

  // Création inline d'un tag depuis la tâche (pas de modale imbriquée).
  const [showNewTag, setShowNewTag] = useState(false);
  const canNudge = useCanAccessFeature('tasks_nudge');
  const [nudging, setNudging] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState<string>(TAG_PALETTE[0]!);

  // Local controlled set of selected tag ids — seeded once when the fetched data arrives.
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
  const [tagTaskId, setTagTaskId] = useState<string | null>(null);

  // Seed selectedTagIds whenever we load a new task or when the fetched tags arrive.
  useEffect(() => {
    if (!task) return;
    const ids = (taskTagMap[task.id] ?? []).map((t) => t.id);
    if (tagTaskId !== task.id || ids.length > 0) {
      setSelectedTagIds(new Set(ids));
      setTagTaskId(task.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, taskTagMap]);

  useEffect(() => {
    if (!task) return;
    setContent(task.content);
    setDescription(task.description ?? '');
    setPriority(task.priority);
    setSectionId(task.section_id);
    const day = extractDueDay(task);
    setDueDate(day);
    setDueTime(day ? ficheDueTime(task.due_datetime) : '');
    setDeadline(task.deadline ?? '');
  }, [task?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!task) return null;

  function handleSave() {
    if (!task) return;
    const trimmed = content.trim();
    if (!trimmed) return;
    if (fields.tags) {
      setTaskTags.mutate({ taskId: task.id, tagIds: Array.from(selectedTagIds) });
    }
    updateTask.mutate(
      {
        taskId: task.id,
        updates: {
          content: trimmed,
          description: description.trim() || null,
          ...(fields.priority ? { priority } : {}),
          ...(fields.sections ? { section_id: sectionId } : {}),
          ...(dueDate
            ? applyFicheDueDay(dueDate, dueTime || null)
            : { due_date: null, due_datetime: null }),
          deadline: deadline || null,
        },
      },
      { onSuccess: onClose },
    );
  }

  function toggleTag(id: string) {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleCreateInlineTag() {
    const trimmed = newTagName.trim();
    if (!trimmed) return;
    const created = await createTag.mutateAsync({ name: trimmed, color: newTagColor });
    // Auto-sélectionne le tag fraîchement créé sur la tâche.
    setSelectedTagIds((prev) => new Set(prev).add(created.id));
    setNewTagName('');
    setNewTagColor(TAG_PALETTE[0]!);
    setShowNewTag(false);
  }

  async function handleNudge() {
    if (!task || nudging) return;
    setNudging(true);
    try {
      const { recipients, emailOk } = await nudgeTask(task.id);
      const who = recipients.join(', ');
      if (emailOk) toast.success(`Relance envoyée à ${who}`);
      else toast.warning(`Notification envoyée à ${who}. Le mail n’est pas parti.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Impossible d’envoyer la relance');
    } finally {
      setNudging(false);
    }
  }

  function handleDelete() {
    if (!task) return;
    requestDelete({ id: task.id, content: task.content });
    onClose();
  }

  function handleToggleComplete(checked: boolean) {
    if (!task) return;
    if (checked) completion.finish(task);
    else completion.reopen(task);
  }

  return (
    <Dialog open={!!task} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl bg-white p-0 gap-0 [&>button]:hidden"
        onOpenAutoFocus={(event) => {
          // Le titre est un <input> : le focus trap le sélectionne en entier
          // à l'ouverture. On pose le focus sur le cadre, pas sur le titre.
          event.preventDefault();
          const node = event.currentTarget;
          if (node instanceof HTMLElement) node.focus();
        }}
      >
        {/* Header */}
        {/* La case « terminer » de l'en-tête ressemblait trop à la case de
            sélection multiple des cartes : remplacée par le bouton Terminer
            du footer (demande Charles). */}
        <div className="flex items-start gap-3 px-6 py-4 border-b border-neutral-200">
          <div className="flex-1 min-w-0">
            <DialogTitle asChild>
              <input
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className={`w-full text-lg font-semibold bg-transparent border-0 outline-none focus:bg-neutral-50 rounded px-1 -mx-1 ${
                  task.is_completed
                    ? 'text-cime-text-muted'
                    : 'text-cime-text-primary'
                }`}
                style={{ fontFamily: 'var(--cime-font-heading)' }}
                placeholder="Titre de la tâche"
              />
            </DialogTitle>
            <DialogDescription className="text-xs text-cime-text-muted mt-1">
              Créée le {formatAppDate(task.created_at)}
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-cime-text-primary p-1"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Assignataires */}
          <div className="flex items-center justify-between gap-3 -mt-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide">
                Intervenants
              </span>
              {assignees.length > 0 ? (
                <AssigneeAvatars assignees={assignees} size="xs" max={6} />
              ) : (
                <span className="text-xs text-cime-text-muted italic">
                  aucun
                </span>
              )}
            </div>
            <TaskAssigneeDropdown taskId={task.id} assignees={assignees} />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide block mb-1.5">
              Description
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ajouter une description, un contexte, des liens…"
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Méta : section + priorité */}
          {(fields.sections || fields.priority) && (
          <div className="grid grid-cols-2 gap-4">
            {fields.sections && (
            <div>
              <label className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide block mb-1.5">
                Section
              </label>
              <select
                value={sectionId ?? ''}
                onChange={(e) => setSectionId(e.target.value || null)}
                className="w-full text-sm border border-neutral-200 rounded-md px-2 py-2 bg-white outline-none focus:border-cime-primary"
              >
                <option value="">— Sans section —</option>
                {sections.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            )}
            {fields.priority && (
            <div>
              <label className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide block mb-1.5">
                Priorité
              </label>
              <div className="inline-flex items-center rounded-md border border-neutral-200 p-0.5">
                {PRIORITIES.map((p) => {
                  const meta = getPriorityMeta(p);
                  const isActive = priority === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPriority(p)}
                      className={`px-2.5 py-1 rounded text-xs font-medium transition ${
                        isActive ? meta.className : 'text-cime-text-muted hover:bg-neutral-50'
                      }`}
                    >
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>
            )}
          </div>
          )}

          {/* Méta : dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide block mb-1.5">
                Échéance
              </label>
              <DatePicker
                value={dueDate}
                onChange={setDueDate}
                showTime
                time={dueTime}
                onTimeChange={setDueTime}
                aria-label="Échéance"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide block mb-1.5">
                Date de relance
              </label>
              <DatePicker
                value={deadline}
                onChange={setDeadline}
                aria-label="Date de relance"
              />
            </div>
          </div>

          {/* Tags */}
          {fields.tags && (
          <div className="pt-2 border-t border-neutral-100">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5" />
                Étiquettes
              </label>
              <Link
                to="/parametres/taches"
                className="text-xs text-cime-text-muted hover:text-cime-primary hover:underline"
              >
                Gérer les étiquettes
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              {allTags.map((tag) => {
                const on = selectedTagIds.has(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    className={`text-xs px-2 py-0.5 rounded-full border transition ${
                      on ? '' : 'text-neutral-600 bg-white hover:bg-neutral-50'
                    }`}
                    style={
                      on
                        ? {
                            backgroundColor: tag.color,
                            borderColor: tag.color,
                            color: readableTextColor(tag.color),
                          }
                        : { borderColor: tag.color }
                    }
                  >
                    {tag.name}
                  </button>
                );
              })}
              {/* Bouton de création inline (pas de modale imbriquée) */}
              {!showNewTag && (
                <button
                  type="button"
                  onClick={() => setShowNewTag(true)}
                  className="text-xs px-2 py-0.5 rounded-full border border-dashed border-neutral-300 text-neutral-500 hover:border-cime-primary hover:text-cime-primary transition inline-flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Nouvelle étiquette
                </button>
              )}
            </div>

            {/* Formulaire de création inline */}
            {showNewTag && (
              <div className="mt-2 p-2.5 rounded-md border border-neutral-200 bg-neutral-50 space-y-2">
                <Input
                  autoFocus
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="Nom de l’étiquette"
                  maxLength={50}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleCreateInlineTag();
                    }
                    if (e.key === 'Escape') setShowNewTag(false);
                  }}
                />
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex gap-1">
                    {TAG_PALETTE.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setNewTagColor(c)}
                        className={`w-5 h-5 rounded-full border-2 ${
                          newTagColor === c ? 'border-neutral-900' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: c }}
                        aria-label={`Couleur ${c}`}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 ml-auto">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowNewTag(false)}
                    >
                      Annuler
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={!newTagName.trim() || createTag.isPending}
                      onClick={() => void handleCreateInlineTag()}
                    >
                      <Plus className="w-3.5 h-3.5" /> Créer
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          <TaskSecondaryAccordion taskId={task.id} dossierId={task.dossier_id} />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-6 py-3 border-t border-neutral-200 bg-neutral-50 rounded-b-lg">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleToggleComplete(!task.is_completed)}
              disabled={completion.isPending}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md transition disabled:opacity-50 ${
                task.is_completed
                  ? 'text-cime-text-muted hover:bg-neutral-100'
                  : 'text-green-700 hover:bg-green-50'
              }`}
            >
              {task.is_completed ? (
                <RotateCcw className="w-3.5 h-3.5" />
              ) : (
                <CheckCircle2 className="w-3.5 h-3.5" />
              )}
              {task.is_completed ? 'Rouvrir' : 'Terminer'}
            </button>
            {canNudge && !task.is_completed && (
              <button
                type="button"
                onClick={handleNudge}
                disabled={nudging}
                title="Envoie une notification et un mail aux personnes en charge"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-cime-text-primary hover:bg-neutral-100 rounded-md transition disabled:opacity-50"
              >
                <img src="/icons/bell.svg" alt="" className="w-3.5 h-3.5 opacity-70" />
                {nudging ? 'Envoi…' : 'Relancer'}
              </button>
            )}
            <button
              type="button"
              onClick={handleDelete}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-md transition"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Supprimer
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose} size="sm">
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              size="sm"
              disabled={!content.trim() || updateTask.isPending}
            >
              {updateTask.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
